// @ts-nocheck
/**
 * Email Verification Page
 * Allows users to verify their email address using a 6-digit code
 * Carbon Design System v11 compliant
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, TextInput, Button, InlineNotification, Tile, Heading, Stack } from '@carbon/react';
import { Email } from '@carbon/icons-react';
import styles from './VerifyEmailPage.module.scss';

// Constants for timing values
const REDIRECT_DELAY_MS = 2000;
const RESEND_COOLDOWN_SECONDS = 60;

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [userEmail, setUserEmail] = useState<string>('');
  const [resendSuccess, setResendSuccess] = useState(false);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Get user email on mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch('/api/v1/auth/me', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setUserEmail(data.email || '');
        }
      } catch (err) {
        console.error('Failed to fetch user info:', err);
      }
    };
    fetchUserInfo();
  }, []);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('/api/v1/auth/email/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ code }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          // Redirect to dashboard or onboarding
          navigate('/dashboard');
        }, REDIRECT_DELAY_MS);
      } else {
        setError(data.message || 'Verification failed. Please check your code.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('/api/v1/auth/email/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setError(null);
        setResendSuccess(true);
        // Auto-dismiss success notification after 3 seconds
        setTimeout(() => {
          setResendSuccess(false);
        }, 3000);
      } else {
        setError(data.message || 'Failed to resend code.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={styles['verify-email-page']}>
        <Tile className={styles['verify-email-tile']}>
          <Stack gap={6}>
            <div className={styles.centered}>
              <Email size={48} className={styles['verify-email-icon']} />
            </div>
            <Heading as="h1" className={styles['verify-email-heading']}>
              Email Verified!
            </Heading>
            <InlineNotification
              kind="success"
              title="Success"
              subtitle="Your email has been verified. Redirecting to dashboard..."
              hideCloseButton
              lowContrast
              aria-live="polite"
            />
          </Stack>
        </Tile>
      </div>
    );
  }

  return (
    <div className={styles['verify-email-page']}>
      <Tile className={styles['verify-email-tile']}>
        <Stack gap={6}>
          <div className={styles.centered}>
            <Email size={48} className={styles['verify-email-icon']} />
          </div>

          <Heading as="h1" className={styles['verify-email-heading']}>
            Verify Your Email Address
          </Heading>

          <p className={styles['verify-email-description']}>
            We sent a 6-digit verification code to <strong>{userEmail || 'your email'}</strong>. Please enter it below
            to verify your account.
          </p>

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError(null)}
              lowContrast
              role="alert"
              aria-live="assertive"
            />
          )}

          {resendSuccess && (
            <InlineNotification
              kind="success"
              title="Code sent"
              subtitle="Check your email for the verification code"
              hideCloseButton
              lowContrast
              role="status"
              aria-live="polite"
            />
          )}

          <Form onSubmit={handleVerify}>
            <Stack gap={6}>
              <TextInput
                id="verification-code"
                labelText="Verification Code"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={e => setCode(e.target.value)}
                maxLength={6}
                required
                disabled={loading}
                helperText="The code is valid for 24 hours"
                autoComplete="off"
                pattern="[0-9]{6}"
                title="Please enter a 6-digit code"
                aria-describedby="verification-code-helper"
              />

              <Button
                type="submit"
                kind="primary"
                disabled={loading || code.length !== 6}
                className={styles['full-width']}
                aria-label="Verify email address with code"
              >
                {loading ? 'Verifying...' : 'Verify Email'}
              </Button>

              <div className={styles.centered}>
                <Button
                  kind="ghost"
                  onClick={handleResend}
                  disabled={loading || resendCooldown > 0}
                  size="sm"
                  aria-label={
                    resendCooldown > 0 ? `Resend code in ${resendCooldown} seconds` : 'Resend verification code'
                  }
                >
                  {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
                </Button>
              </div>
            </Stack>
          </Form>

          <div className={`${styles.centered} ${styles['mt-spacing-05']}`}>
            <p className={styles['verify-email-footer']}>
              Didn't receive the email? Check your spam folder or{' '}
              <Button
                kind="ghost"
                size="sm"
                onClick={handleResend}
                disabled={resendCooldown > 0}
                className={styles['verify-email-link']}
                aria-label="Resend verification code"
              >
                resend the code
              </Button>
              .
            </p>
          </div>
        </Stack>
      </Tile>
    </div>
  );
};

export default VerifyEmailPage;
